import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import AccountUsersTableRowComponent from 'webapp/components/account/users/table-row';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import NoResultsUsersComponent from 'webapp/components/no-results/users';
import TableSorterComponent from 'webapp/components/table-sorter';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type { DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortUser from 'webapp/models-custom/filter-sort/user';
import type User from 'webapp/models-custom/user';

interface Args {
	model: User[];
	onAdd: () => void;
}

export default class AccountUsersTableComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('navigation') declare navigationService: Service.Navigation;

	visibleCheckContainerNode: HTMLElement | null = null;
	filterSort: FilterSortUser;
	batchSelect: BatchSelect<User>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortUser({
			getAllItems: () => {
				return this.allItems;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results.filter((user) => !user.isMe && !user.isParentUser);
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});
	}

	get isAnyUsersAdded() {
		return this.allItems.length > 0;
	}

	get allItems() {
		return this.args.model;
	}

	get sortedUserList() {
		const userList: User[] = [],
			pendingList: User[] = [];

		this.filterSort.results.forEach((user: User) => {
			if (user.model.activated) {
				userList.push(user);
			} else {
				pendingList.push(user);
			}
		});

		return [...pendingList, ...userList];
	}

	get addButtonContainerNode() {
		return isAlive(this) && !this.selectedItems.length
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems() {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<DeleteAction>[] {
		return [this.batchSelect.deleteOption];
	}

	doBatchAction(actionName: DeleteAction) {
		return this[actionName]();
	}

	@action
	batchDelete() {
		return this.mmUsersService.batchDelete(this.selectedItems);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<div class="bottom-padding">

			{{#if this.isAnyUsersAdded}}
				<FilterLineProxyComponent @model={{this.filterSort}} />

				<FilterNoResultsComponent @model={{this.filterSort}} />
			{{/if}}

			{{#unless this.isAnyUsersAdded}}
				<NoResultsUsersComponent @onCreate={{@onAdd}} />
			{{/unless}}

			<table
				id="usersList"
				class="flexible-table bigtable has-checkbox has-avatar
					{{if this.filterSort.noResults 'hide'}}"
				{{visibleCheckContainer}}
			>
				<colgroup>
					<col class="cc" />
					<col class="h0" />
					<col width="30%" />
					<col />
					<col />
					<col />
					<col />
					<col class="button-ct" />
				</colgroup>
				<thead>
					<tr>
						<CommonBatchTableHeaderComponent @batchSelect={{this.batchSelect}} />
						<th class="h0">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="name"
								@name="Name"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th></th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="created"
								@name="Joined"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th>
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="lastSeen"
								@name="Last seen"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th>
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="allowedBoards.length"
								@name="Shared dashboards"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th>
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="level"
								@name="Role"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.sortedUserList as |result|}}
						<AccountUsersTableRowComponent
							@model={{result}}
							@batchSelect={{this.batchSelect}}
						/>
					{{/each}}
				</tbody>
			</table>

			<TableAddNewItemComponent
				@title="Add user"
				@addNewItem={{@onAdd}}
				@model={{this.filterSort}}
			/>
		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent @text="Invite teammate" @callTrigger={{@onAdd}} />
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
